<template>
  <q-btn v-if="canUseCamera" :color="color" :outline="outline" :text-color="textColor" @click="onClick">
    <div class="row items-center no-wrap">
      <div><q-icon :class="{ 'q-mr-xs': label }" :name="icon" /></div>
      <div v-if="label">{{ label }}</div>
    </div>
    <q-popup-proxy
      v-if="$q.platform.is.capacitor && mode === 'dual'"
      breakpoint="0"
      :model-value="showScanOptions"
      no-parent-event
      @hide="showScanOptions = false"
    >
      <q-btn-group>
        <PrimaryInlineButton padding="sm sm xs" @click="scanCode">
          <div class="column items-center">
            <q-icon class="q-my-xs" name="qr_code_scanner" />
            <div>QR/Bar Code</div>
          </div>
        </PrimaryInlineButton>
        <q-separator vertical />
        <PrimaryInlineButton padding="sm sm xs" @click="scanText">
          <div class="column items-center">
            <q-icon class="q-my-xs" name="document_scanner" />
            <div>Text Scan</div>
          </div>
        </PrimaryInlineButton>
      </q-btn-group>
    </q-popup-proxy>
    <ScanDialog
      v-if="showScanDialog"
      :allow-mode-switch="allowModeSwitch"
      :default-scan-mode="scanMode"
      :validation="validation"
      @code-scan="(_, text) => onCodeScan(text)"
      @hide="showScanDialog = false"
      @text-scan="onTextScan"
    />
    <VerifyVINDialog v-if="showVerifyVinDialog" :vin="vin" @hide="showVerifyVinDialog = false" @ok="onOk" />
    <ScannedTextDialog
      v-if="showScannedTextDialog"
      :image-data-url="scannedImageDataUrl"
      :preparing="isOpeningCamera"
      :processing="isProcessingText"
      :text-blocks="scannedTextBlocks"
      @hide="showScannedTextDialog = false"
      @ok="onOk"
      @retry="scanText"
    />
  </q-btn>
</template>

<script setup lang="ts">
import { useQuasar } from 'quasar';
import { computed, ref, type Ref } from 'vue';
import { useStore } from 'vuex';
import PrimaryInlineButton from 'components/buttons/PrimaryInlineButton.vue';
import VerifyVINDialog from 'components/scanner/VerifyVINDialog.vue';
import { codeScan, textScan } from 'src/services/scanner';
import { setTimeoutPromise } from 'src/services/setTimeout';
import {
  mapForbidden,
  minorNormalization,
  removeBarcodeSentinels,
  stripNonAlphaNumeric,
  validateVin,
} from 'src/services/vin';
import ScannedTextDialog from './ScannedTextDialog.vue';

const props = withDefaults(
  defineProps<{
    allowModeSwitch?: boolean;
    color?: string;
    defaultScanMode?: string;
    icon?: string;
    label?: string;
    mode?: 'dual' | 'code' | 'text';
    outline?: boolean;
    textColor?: string;
    validation?: string;
  }>(),
  {
    allowModeSwitch: true,
    color: 'white',
    icon: 'photo_camera',
    mode: 'dual',
    textColor: 'black',
  }
);

const emit = defineEmits(['scan']);

const showScanDialog = ref(false);
const showScanOptions = ref(false);
const showVerifyVinDialog = ref(false);
const isOpeningCamera = ref(false);
const isProcessingText = ref(false);

const store = useStore();

const canUseCamera = computed(() => store.state.env.canUseCamera);
const scanMode = computed(() => props.mode || props.defaultScanMode);

const $q = useQuasar();

function onClick() {
  if ($q.platform.is.capacitor) {
    switch (props.mode) {
      case 'dual':
        showScanOptions.value = true;
        break;
      case 'code':
        scanCode();
        break;
      case 'text':
        scanText();
        break;
    }
  } else {
    showScanDialog.value = true;
  }
}

const vin = ref('');
function onOk(text: string) {
  emit('scan', new Event('ScanEvent'), text);
}

function cleanVin(text: string) {
  return removeBarcodeSentinels(minorNormalization(text));
}

/**
 * Native QR/Barcode scanning
 */
async function scanCode() {
  showScanOptions.value = false;
  let text = await codeScan();
  onCodeScan(text);
}

async function scanText() {
  showScanOptions.value = false;

  openScannedTextDialog();
  isOpeningCamera.value = true;
  await setTimeoutPromise(250);

  const { status, textBlocks } = await textScan(async (imageDataUrl: string) => {
    if (!showScannedTextDialog.value) {
      // User closed the dialog, exit early
      return false;
    }
    isOpeningCamera.value = false;
    isProcessingText.value = true;
    scannedImageDataUrl.value = imageDataUrl;
    await setTimeoutPromise(250);
    return true;
  });

  // User canceled the photo/closed the dialog
  if (status === 'canceled' || !showScannedTextDialog.value) {
    isProcessingText.value = false;
    showScannedTextDialog.value = false;
    return;
  }

  onScan(textBlocks);
}

function openScannedTextDialog() {
  scannedImageDataUrl.value = '';
  scannedTextBlocks.value = [];
  showScannedTextDialog.value = true;
}

function onCodeScan(text: string) {
  if (props.validation === 'vin') {
    text = cleanVin(text);
  }
  onOk(text);
}

function onTextScan(imageDataUrl: string, foundTextBlocks?: string[]) {
  scannedImageDataUrl.value = imageDataUrl;
  onScan(foundTextBlocks);
}

const showScannedTextDialog = ref(false);
const scannedTextBlocks: Ref<{ text: string; valid?: boolean }[]> = ref([]);
const scannedImageDataUrl = ref('');
async function onScan(textBlocks?: string[]) {
  showScanDialog.value = false;
  openScannedTextDialog();

  await setTimeoutPromise(250);

  isProcessingText.value = false;

  if (textBlocks.length === 1) {
    showScannedTextDialog.value = false;
    // Return the valid text block found
    onOk(textBlocks[0]);
    return;
  }

  if (props.validation === 'vin') {
    // Validate each text block to see if it's a VIN
    const likelyVins = [] as string[];
    const otherTextBlocks = [] as string[];
    textBlocks.forEach((textBlock) => {
      const cleanedText = stripNonAlphaNumeric(mapForbidden(minorNormalization(textBlock)));
      const { valid } = validateVin(cleanedText);
      if (valid && !likelyVins.includes(cleanedText)) {
        likelyVins.push(cleanedText);
      } else if (!valid && !otherTextBlocks.includes(cleanedText)) {
        otherTextBlocks.push(cleanedText);
      }
    });
    // Put probably VINs at the top (and filter duplicates)
    scannedTextBlocks.value = [
      ...likelyVins.map((text) => ({
        text,
        valid: true,
      })),
      ...textBlocks.map((text) => ({
        text,
        valid: false,
      })),
    ];
    return;
  }

  // Have user select from the text blocks found
  scannedTextBlocks.value = [...textBlocks].map((text) => ({ text }));
}
</script>
